import type { PositionSnapshot } from '@strategy4kids/protocol';
import { parsePlacement, squareToIndex } from './coords.js';
import type { GameAdapter } from './game-adapter.js';
import { buildShieldMap } from './shields.js';

const sq = (file: number, rank: number): string => `${String.fromCharCode(97 + file)}${rank}`;
const fileOf = (s: string): number => s.charCodeAt(0) - 97;
const rankOf = (s: string): number => Number(s.slice(1));

/**
 * The squares a piece passes over on the way from `from` to `to`, exclusive of
 * both ends. Straight and diagonal moves yield the squares in between (a
 * checkers jump yields the jumped square). A knight yields its L: two squares
 * along the long axis, then the turn to the destination. Anything else (a
 * one-step move) has no path.
 */
export function pathSquares(from: string, to: string): string[] {
  const df = fileOf(to) - fileOf(from);
  const dr = rankOf(to) - rankOf(from);
  const out: string[] = [];
  const adf = Math.abs(df);
  const adr = Math.abs(dr);
  if ((adf === 1 && adr === 2) || (adf === 2 && adr === 1)) {
    const stepF = adf === 2 ? Math.sign(df) : 0;
    const stepR = adr === 2 ? Math.sign(dr) : 0;
    for (const n of [1, 2]) out.push(sq(fileOf(from) + stepF * n, rankOf(from) + stepR * n));
    return out;
  }
  if (df === 0 || dr === 0 || adf === adr) {
    const steps = Math.max(adf, adr);
    for (let n = 1; n < steps; n++) {
      out.push(sq(fileOf(from) + Math.sign(df) * n, rankOf(from) + Math.sign(dr) * n));
    }
  }
  return out;
}

/** Union of the paths to every destination, minus the target squares themselves. */
export function pathsToTargets(from: string, destinations: readonly string[]): string[] {
  const ends = new Set(destinations);
  const seen = new Set<string>();
  for (const d of destinations) for (const s of pathSquares(from, d)) if (!ends.has(s)) seen.add(s);
  return [...seen];
}

export type MoveRisk = 'lost' | 'trade';

/** Rough material value by piece letter (case-insensitive); checkers man/king included. */
const VALUE: Record<string, number> = { p: 1, n: 3, b: 3, r: 5, q: 9, m: 1, x: 2 };
const valueOf = (char: string | null | undefined): number =>
  char ? (VALUE[char.toLowerCase()] ?? 0) : 0;

/**
 * Risk of each destination for the piece moving from `from`, evaluated on the
 * position AFTER the move:
 *  - `lost`  — attacked and no defender: a certain loss (or a sacrifice).
 *  - `trade` — defended, but attacked by a cheaper piece than the net value the
 *              move puts at stake (mover value minus anything it captures), so
 *              the recapture still wins material. A king never counts as the
 *              cheap attacker (it cannot take a defended piece).
 * Squares with no risk are absent. Defender semantics are the shield map's (a
 * king is not a defender; pins are not modelled), matching the shield badges.
 */
export function assessTargets(
  adapter: GameAdapter,
  pos: PositionSnapshot,
  from: string,
  destinations: readonly string[],
): Map<string, MoveRisk> {
  const color = pos.turn;
  const before = parsePlacement(pos.fen);
  const out = new Map<string, MoveRisk>();
  for (const to of destinations) {
    const after = adapter.makeMove(pos, from, to);
    const placement = parsePlacement(after.fen);
    const map = buildShieldMap(placement, adapter.attackGen(), color);
    const idx = squareToIndex(to);
    const attackers = map.threatened.get(idx) ?? [];
    if (attackers.length === 0) continue;
    if (!(map.defended.get(idx)?.length ?? 0)) {
      out.set(to, 'lost');
      continue;
    }
    const stake = valueOf(placement[idx]) - valueOf(before[idx]);
    const cheapest = Math.min(
      ...attackers
        .map((a) => placement[a])
        .filter((c) => c != null && c.toLowerCase() !== 'k')
        .map(valueOf),
    );
    if (cheapest < stake) out.set(to, 'trade');
  }
  return out;
}

/** Destinations that are a certain loss (attacked, nothing defends). */
export function unsafeTargets(
  adapter: GameAdapter,
  pos: PositionSnapshot,
  from: string,
  destinations: readonly string[],
): string[] {
  const out: string[] = [];
  for (const [sq, risk] of assessTargets(adapter, pos, from, destinations)) {
    if (risk === 'lost') out.push(sq);
  }
  return out;
}
